import { DocumentNode, print } from 'graphql';
import { useState, useCallback, useMemo } from 'react';
import { enqueueMutation } from 'services/outbox';
import { OperationContext, OperationResult, AnyVariables, useClient } from 'urql';
import { MutationMessages, createCombinedError } from 'utils/offlineQueryMutation';
import useOnlineStatus, { OnlineStatus } from './useOnlineStatus';
import { SnackbarVariant, useSnackbar } from './useSnackBar';

const QUEUED_MESSAGE = 'Your request has been queued and will be processed when you are back online.';
const QUEUE_FAILED_MESSAGE = 'Your request could not be saved for later. Please try again.';

export type OfflineMutationResult<TData, TVariables extends AnyVariables> = OperationResult<TData, TVariables> & {
  // Saved to the outbox instead of sent; there is no data until it syncs.
  queued: boolean;
};

const createMockOperationResult = <TData, TVariables extends AnyVariables>(
  overrides: Partial<OfflineMutationResult<TData, TVariables>> = {},
): OfflineMutationResult<TData, TVariables> => ({
  data: undefined,
  error: undefined,
  stale: false,
  hasNext: false, // Required by OperationResult
  extensions: {},
  operation: {} as any, // Minimal mock, could refine with Operation type if needed
  queued: false,
  ...overrides,
});

const toCombinedError = (error: unknown) => (error instanceof Error ? createCombinedError(error) : undefined);

const useOfflineMutation = <TData = any, TVariables extends Record<string, unknown> = { input: any }>(
  key: string,
  mutationNode: DocumentNode,
  useOffline = true,
) => {
  const [lastResult, setLastResult] = useState<Pick<OperationResult<TData>, 'data' | 'error'>>({});
  const [inFlightCount, setInFlightCount] = useState(0);
  const isOnline = useOnlineStatus() !== OnlineStatus.Offline;
  const client = useClient();
  const { showSnackbar } = useSnackbar();

  const sendMutation = useCallback(
    async (variables: TVariables, context?: Partial<OperationContext>) => {
      try {
        const result = await client.mutation<TData, TVariables>(mutationNode, variables, context).toPromise();
        setLastResult({ data: result.data, error: result.error });
        return { ...result, queued: false };
      } catch (error) {
        const combinedError = toCombinedError(error);
        setLastResult({ error: combinedError });
        return createMockOperationResult<TData, TVariables>({ error: combinedError });
      }
    },
    [client, mutationNode],
  );

  const queueMutation = useCallback(
    async (variables: TVariables, messages: MutationMessages, context?: Partial<OperationContext>) => {
      try {
        await enqueueMutation({
          key,
          query: print(mutationNode),
          variables,
          messages,
          additionalTypenames: context?.additionalTypenames,
        });
      } catch (error) {
        const combinedError = toCombinedError(error);
        setLastResult({ error: combinedError });
        showSnackbar({ message: QUEUE_FAILED_MESSAGE, autoHideDuration: 4000 }, SnackbarVariant.ERROR);
        return createMockOperationResult<TData, TVariables>({ error: combinedError });
      }
      setLastResult({});
      showSnackbar({ message: QUEUED_MESSAGE, autoHideDuration: 2000 }, SnackbarVariant.ALERT);
      return createMockOperationResult<TData, TVariables>({ queued: true });
    },
    [key, mutationNode, showSnackbar],
  );

  const executeMutation = useCallback(
    async (
      variables: TVariables,
      messages: MutationMessages,
      context?: Partial<OperationContext>,
    ): Promise<OfflineMutationResult<TData, TVariables>> => {
      setInFlightCount((count) => count + 1);
      try {
        // With useOffline off the mutation is always sent, never queued.
        return !useOffline || isOnline
          ? await sendMutation(variables, context)
          : await queueMutation(variables, messages, context);
      } finally {
        setInFlightCount((count) => count - 1);
      }
    },
    [useOffline, isOnline, sendMutation, queueMutation],
  );

  const mutationState = useMemo(
    () => ({
      ...lastResult,
      stale: false,
      hasNext: false,
      extensions: {},
      operation: undefined,
      fetching: inFlightCount > 0,
    }),
    [lastResult, inFlightCount],
  );

  return [mutationState, executeMutation] as const;
};

export default useOfflineMutation;
